import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { importExcel } from '../lib/api';
import { toast } from 'sonner';

const ImportPage = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xlsm|xls)$/i.test(f.name)) {
      toast.error('Formato non supportato: serve un file Excel (.xlsx/.xlsm)');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      toast.error('File troppo grande (max 20 MB)');
      return;
    }
    setFile(f);
    setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files[0];
    handleFile(f);
  };

  const startUpload = async () => {
    if (!file) return;
    setUploading(true);
    setProgress(20);
    try {
      // Fake progress while server crunches
      const ticker = setInterval(() => setProgress((p) => Math.min(p + 8, 90)), 250);
      const res = await importExcel(file);
      clearInterval(ticker);
      setProgress(100);
      setResult(res.data);
      toast.success(`Import completato: ${res.data.transactions_added} transazioni`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante import');
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setProgress(0);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-in" data-testid="import-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Indietro
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Importa da Excel</h1>
        <p className="text-muted-foreground">
          Carica un file <code className="text-xs px-1.5 py-0.5 rounded bg-muted">.xlsx</code> o <code className="text-xs px-1.5 py-0.5 rounded bg-muted">.xlsm</code> per
          popolare automaticamente categorie e transazioni storiche.
        </p>
      </div>

      {!result && (
        <Card>
          <CardHeader>
            <CardTitle>Carica file</CardTitle>
            <CardDescription>
              Il sistema rileva automaticamente i fogli con le transazioni e cerca colonne come Data/Scadenza,
              Tipo, Categoria, Descrizione, Importo. Le righe duplicate vengono ignorate.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
              }`}
              data-testid="dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="file-input"
              />
              {file ? (
                <div className="flex flex-col items-center">
                  <FileSpreadsheet className="w-12 h-12 text-primary mb-3" strokeWidth={1.5} />
                  <p className="font-semibold">{file.name}</p>
                  <p className="text-sm text-muted-foreground">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-3">
                    <Upload className="w-6 h-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <p className="font-semibold mb-1">Trascina il file qui</p>
                  <p className="text-sm text-muted-foreground">o clicca per selezionarlo · max 20 MB</p>
                </div>
              )}
            </div>

            {uploading && (
              <div className="mt-6 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Elaborazione in corso
                  </span>
                  <span className="text-muted-foreground tabular-nums">{progress}%</span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {file && !uploading && (
              <div className="mt-6 flex gap-2 justify-end">
                <Button variant="ghost" onClick={reset}>Cambia file</Button>
                <Button onClick={startUpload} data-testid="start-import-btn">
                  Importa <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {result && (
        <Card data-testid="import-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" /> Import completato
            </CardTitle>
            <CardDescription>Riepilogo dell'operazione.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase">Transazioni</p>
                <p className="text-2xl font-bold tabular-nums text-emerald-600">{result.transactions_added}</p>
                <p className="text-xs text-muted-foreground">aggiunte</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase">Già presenti</p>
                <p className="text-2xl font-bold tabular-nums">{result.transactions_skipped}</p>
                <p className="text-xs text-muted-foreground">saltate</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="text-xs text-muted-foreground uppercase">Categorie</p>
                <p className="text-2xl font-bold tabular-nums text-primary">{result.categories_added}</p>
                <p className="text-xs text-muted-foreground">create</p>
              </div>
            </div>

            {result.skipped_sheets?.length > 0 && (
              <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/60 dark:bg-amber-950/20 p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900 dark:text-amber-200">
                      Fogli saltati ({result.skipped_sheets.length})
                    </p>
                    <p className="text-amber-800/80 dark:text-amber-300/80">
                      Non sono stati riconosciuti come tabelle di transazioni:
                    </p>
                    <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1 font-mono">
                      {result.skipped_sheets.slice(0, 12).join(', ')}{result.skipped_sheets.length > 12 ? ', ...' : ''}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <Button variant="ghost" onClick={reset}>Importa un altro file</Button>
              <Button onClick={() => navigate('/transactions')} data-testid="goto-transactions-btn">
                Vedi transazioni <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportPage;
